import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/control/Button";
import {
  BookIcon,
  ChaptersIcon,
  CheckIcon,
  ChevronDownIcon,
  GuidanceIcon,
  HeadphonesIcon,
  MicrophoneIcon,
  ReviewIcon,
} from "../../components/icons";
import { LanguageSwitcher } from "../../components/layout/LanguageSwitcher";
import { SignOutButton } from "../../components/layout/SignOutButton";
import { ThemeSwitcher } from "../../components/layout/ThemeSwitcher";
import { WorkspaceActionHost } from "../../components/layout/WorkspaceActions";
import { Dialog } from "../../components/overlay/Dialog";
import { flushWorkspaceDrafts } from "../../drafts/useWorkspaceDraft";
import { useBookDetail } from "./bookContext";
import { BOOK_STEPS, type StepKey } from "./keys";
import { step6Segment, stepLock } from "./steps";
import { nextBookAction } from "./workspace";
import "./workspace-navigation.css";

const STAGE_ICONS = {
  step1: BookIcon,
  step2: ChaptersIcon,
  step5: MicrophoneIcon,
  step3: GuidanceIcon,
  step4: ReviewIcon,
  step6: HeadphonesIcon,
} satisfies Record<StepKey, typeof BookIcon>;

export function BookWorkspace({
  children,
  banner,
  flush,
}: {
  children: ReactNode;
  banner: ReactNode;
  flush: boolean;
}) {
  const book = useBookDetail();
  const { t } = useTranslation("nav");
  const location = useLocation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [chaptersOpen, setChaptersOpen] = useState(location.pathname.includes("/review"));
  const [locked, setLocked] = useState<StepKey | null>(null);
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const active =
    BOOK_STEPS.find((s) =>
      s.key === "step6"
        ? /\/(render|progress|audiobook)(?:\/|$)/.test(location.pathname)
        : location.pathname.includes(`/${s.segment}`),
    )?.key ?? "step1";
  // biome-ignore lint/correctness/useExhaustiveDependencies: chapter route changes also close the mobile navigation.
  useEffect(() => {
    setOpen(false);
    setLocked(null);
    if (active === "step4") setChaptersOpen(true);
  }, [location.pathname, active]);
  useEffect(() => {
    if (active === "step4")
      sessionStorage.setItem(`review-path:${book.id}`, location.pathname + location.search);
  }, [active, book.id, location.pathname, location.search]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a route change can mount a new target for the same fragment.
  useEffect(() => {
    if (location.hash)
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [location.hash, location.pathname]);
  const next = book.nextAction ?? nextBookAction(book);
  const keyFor: Record<StepKey, string> = {
    step1: "metadata",
    step2: "chapters",
    step3: "guidance",
    step4: "chapter-",
    step5: "casting",
    step6: "settings",
  };
  const status = (key: StepKey): NavigationStatus => {
    if (
      book.drafts?.some((d) =>
        key === "step4" ? d.key.startsWith("chapter-") : d.key === keyFor[key],
      )
    )
      return "draft";
    if (stepLock(key, book)) return "preparing";
    if (
      key === "step4" &&
      book.chapters.some((c) => !c.excluded && c.state === "inReview" && !c.approvedAt)
    )
      return "attention";
    const segment = BOOK_STEPS.find((s) => s.key === key)?.segment;
    if (segment && next.path?.includes(`/${segment}`)) return "attention";
    if (
      (key === "step1" && (book.metadataConfirmedAt ?? book.confirmedAt)) ||
      (key === "step2" && book.confirmedAt) ||
      (key === "step5" && book.casting.narrator) ||
      (key === "step6" && book.audiobook && !book.audiobook.stale)
    )
      return "complete";
    return "ready";
  };
  const stages = BOOK_STEPS.map((step) => {
    const reason = stepLock(step.key, book);
    const remembered =
      step.key === "step4" ? sessionStorage.getItem(`review-path:${book.id}`) : null;
    const validRemembered =
      remembered &&
      book.chapters.some((c) => remembered.split("?")[0] === `/books/${book.id}/review/${c.id}`);
    const path = validRemembered
      ? remembered
      : `/books/${book.id}/${step.key === "step6" ? step6Segment(book) : step.segment}`;
    const className = "workspace-stage";
    const StageIcon = STAGE_ICONS[step.key];
    const label = (
      <NavigationLabel key={step.key} status={status(step.key)} icon={<StageIcon size={18} />}>
        {t(`nav:${step.key}`)}
      </NavigationLabel>
    );

    return { step, reason, path, className, label };
  });
  const navigation = (
    <div className="flex h-full min-h-0 flex-col bg-ground">
      <div className="max-h-[28dvh] shrink-0 overflow-y-auto border-b border-line px-5 py-5">
        <Link className="text-data text-ink-2 hover:text-ink hover:underline" to="/">
          {t("workspace.library")}
        </Link>
        <Link
          className="mt-4 block break-words font-serif text-title font-semibold"
          to={`/books/${book.id}/overview`}
        >
          {book.title ?? book.source.filename}
        </Link>
        {book.authorName ? (
          <p className="mt-2 break-words text-data text-ink-2">{book.authorName}</p>
        ) : null}
      </div>
      <nav
        aria-label={t("workspace.stages")}
        className="workspace-navigation min-h-0 flex-1 space-y-1 overflow-y-auto p-3"
      >
        {stages.map(({ step, reason, path, className, label }) => {
          return (
            <div key={step.key}>
              {reason ? (
                <button
                  type="button"
                  data-step={step.key}
                  aria-current={active === step.key ? "page" : undefined}
                  className={className}
                  onClick={() => setLocked(step.key)}
                >
                  {label}
                </button>
              ) : (
                <Link
                  data-step={step.key}
                  to={path}
                  className={className}
                  aria-current={active === step.key ? "page" : undefined}
                >
                  {label}
                </Link>
              )}
              {locked === step.key && reason ? (
                <div className="px-3 pb-3 text-data" role="status">
                  <p>{t(`stepLock.${reason}`)}</p>
                  <Link
                    className="mt-2 inline-block text-accent underline"
                    to={next.path ?? `/books/${book.id}/overview`}
                  >
                    {t("workspace.resolve")}
                  </Link>
                </div>
              ) : null}
              {step.key === "step4" && book.chapters.some((c) => c.versionCount > 0) ? (
                <>
                  <button
                    type="button"
                    className="workspace-disclosure flex min-h-10 w-full items-center gap-2 rounded-md px-3 text-left text-data text-ink-2 hover:bg-surface hover:text-ink"
                    aria-label={
                      chaptersOpen ? t("workspace.hideChapters") : t("workspace.showChapters")
                    }
                    aria-expanded={chaptersOpen}
                    onClick={() => setChaptersOpen((v) => !v)}
                  >
                    <ChevronDownIcon className="workspace-chevron shrink-0" size={14} />
                    {t("nav:step2")}
                  </button>
                  {chaptersOpen ? (
                    <ol className="workspace-chapters ml-6 space-y-1 pb-2">
                      {book.chapters
                        .filter((c) => !c.excluded)
                        .map((chapter) => (
                          <li key={chapter.id}>
                            <Link
                              to={`/books/${book.id}/review/${chapter.id}`}
                              aria-current={
                                location.pathname.endsWith(chapter.id) ? "page" : undefined
                              }
                              className="workspace-chapter block break-words rounded-md border border-transparent px-3 py-2 text-data text-ink-2 hover:bg-surface hover:text-ink"
                            >
                              <NavigationLabel
                                status={
                                  book.drafts?.some((d) => d.key === `chapter-${chapter.id}`)
                                    ? "draft"
                                    : chapter.approvedAt
                                      ? "complete"
                                      : chapter.state === "inReview"
                                        ? "attention"
                                        : "preparing"
                                }
                              >
                                {chapter.number}. {chapter.title}
                              </NavigationLabel>
                            </Link>
                          </li>
                        ))}
                    </ol>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}
      </nav>
      <div className="flex shrink-0 flex-col gap-3 border-t border-line p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-data text-ink-2">{t("common:appearance.label")}</span>
          <ThemeSwitcher />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <LanguageSwitcher />
          <SignOutButton />
        </div>
      </div>
    </div>
  );
  return (
    <WorkspaceActionHost value={host}>
      <div
        className="flex h-dvh min-w-0 overflow-hidden bg-ground"
        onBlurCapture={() => {
          void flushWorkspaceDrafts(client, book.id).catch(() => {});
        }}
        onClickCapture={(event) => {
          const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
          if (
            !link ||
            link.target ||
            link.hasAttribute("download") ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
          )
            return;
          const url = new URL(link.href, window.location.href);
          if (url.origin !== window.location.origin || url.pathname.startsWith("/api/")) return;
          event.preventDefault();
          event.stopPropagation();
          void flushWorkspaceDrafts(client, book.id)
            .then(() => {
              setOpen(false);
              setLocked(null);
              return navigate(url.pathname + url.search + url.hash);
            })
            .catch(() => {});
        }}
      >
        <aside className="hidden w-72 shrink-0 border-r border-line bg-ground xl:block">
          {navigation}
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3 xl:hidden">
            <span className="min-w-0 truncate font-medium">
              {book.title ?? book.source.filename}
            </span>
            <Button variant="secondary" onClick={() => setOpen(true)}>
              {t(`nav:${active}`)}
            </Button>
          </header>
          <div className="shrink-0">{banner}</div>
          <main
            className={
              flush
                ? "flex min-h-0 min-w-0 flex-1 overflow-hidden"
                : "min-h-0 min-w-0 flex-1 overflow-auto px-4 py-5 sm:px-6 xl:px-8"
            }
          >
            {children}
          </main>
          <div ref={setHost} className="max-h-[40dvh] shrink-0 overflow-auto" />
        </div>
        <Dialog drawer open={open} title={t("workspace.stages")} onClose={() => setOpen(false)}>
          <div className="h-full min-h-0">{navigation}</div>
        </Dialog>
      </div>
    </WorkspaceActionHost>
  );
}

/** Location is carried by the row; production status has its own accessible label. */
type NavigationStatus = "draft" | "preparing" | "attention" | "complete" | "ready";

function NavigationLabel({
  children,
  status,
  icon,
}: {
  children: ReactNode;
  status: NavigationStatus;
  icon?: ReactNode;
}) {
  const { t } = useTranslation("nav");
  const statusLabels: Record<NavigationStatus, string> = {
    draft: t("workspace.draft"),
    preparing: t("workspace.preparing"),
    attention: t("workspace.attention"),
    complete: t("workspace.complete"),
    ready: t("workspace.ready"),
  };
  return (
    <span className={icon ? "grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3" : "block"}>
      {icon ? <span className="workspace-stage-icon mt-0.5">{icon}</span> : null}
      <span className="min-w-0">
        <span className="flex items-start justify-between gap-3">
          <span className="workspace-nav-label min-w-0">{children}</span>
          {status === "complete" ? (
            <CheckIcon className="workspace-complete mt-0.5 shrink-0" size={14} />
          ) : null}
        </span>
        <span
          className={
            status === "complete" || status === "ready"
              ? "sr-only"
              : "workspace-status mt-1 block text-data font-normal text-ink-2"
          }
        >
          {statusLabels[status]}
        </span>
      </span>
    </span>
  );
}
